import React, { useState, useEffect } from 'react';
import { withTranslation } from '../../Translator';
import StatusBar from '../../StatusBar';
import { addToast, ToastContainer } from '../../Toast';
import { ManagePaymentModal } from './components/ManagePaymentModal/ManagePaymentModal';

const API_URLS = {
  CURRENT_SUBSCRIPTION: '/api/subscription/v1/tier',
  MANAGE_PAYMENT: '/api/subscription/v1/tier/management-url',
};

function getRequestConfig({ jwt }) {
  const headers = {
    'Content-Type': 'application/vnd.api+json',
    Authorization: `Bearer ${jwt}`,
  };

  return {
    method: 'GET',
    headers,
  };
}

function useCurrentSubscriptionInfo({ jwt }) {
  const [data, setData] = useState({
    currentSubscription: null,
  });

  useEffect(() => {
    fetch(API_URLS.CURRENT_SUBSCRIPTION, getRequestConfig({ jwt }))
      .then(resp => resp.json())
      .then((resp) => {
        setData({ currentSubscription: resp });
      })
      .catch(() => {});
  }, [jwt]);

  return data;
}

function useManagePayment({ jwt }) {
  const [isLoading, setIsLoading] = useState(false);

  const fetchManagePaymentUrl = async () => {
    setIsLoading(true);

    try {
      const resp = await fetch(API_URLS.MANAGE_PAYMENT, getRequestConfig({ jwt }));
      const result = await resp.json();

      return result.update_payment_method;
    } catch {
      throw Error();
    } finally {
      setIsLoading(false);
    }
  };

  return { fetchManagePaymentUrl, isLoading };
}

function PaymentFailedBanner({ jwt, t } = {}) {
  const [isPaymentFailed, setIsPaymentFailed] = useState(false);
  const [showManagePaymentModal, setShowManagePaymentModal] = useState(false);
  const { currentSubscription } = useCurrentSubscriptionInfo({ jwt });
  const { fetchManagePaymentUrl, isLoading } = useManagePayment({ jwt });

  useEffect(() => {
    if (currentSubscription && currentSubscription.status === 'past_due') {
      setIsPaymentFailed(true);
    }
  }, [currentSubscription]);

  if (isPaymentFailed) {
    return (
      <>
        <ToastContainer t={t} />
        <StatusBar
          text={t('PaymentFailedBanner.banner-error.text')}
          type="error"
          linkText={t('PaymentFailedBanner.banner-error.button-text')}
          linkLeftIcon="external-link"
          onLinkClick={() => setShowManagePaymentModal(true)}
          linkType="button"
        />
        {showManagePaymentModal && (
          <ManagePaymentModal
            t={t}
            isLoading={isLoading}
            onConfirm={async () => {
              try {
                const url = await fetchManagePaymentUrl();

                if (url) {
                  window.open(url, '_blank');
                  setShowManagePaymentModal(false);
                }
              } catch {
                addToast({
                  id: '1',
                  type: 'error',
                  text: t('PaymentFailedBanner.manage-payment-modal.toast-error-text'),
                  autoCloseSeconds: 5,
                });
              }
            }}
            onClose={() => setShowManagePaymentModal(false)}
          />
        )}
      </>
    );
  }

  return null;
}

const enhanced = withTranslation('components')(PaymentFailedBanner);

export { PaymentFailedBanner as PureComponent };
export default enhanced;
